import React, { useEffect, useState } from 'react';

import { Button, Chip, CircularProgress, Popover, Stack, TextField, Typography } from '@mui/material';

// Stored through the existing feedback comment field until le-data exposes a first-class reason enum.
// Keep the `reason:<snake_case>` token stable for downstream parsing.
const FEEDBACK_DOWN_REASONS = [
  { label: 'Wrong numbers', value: 'wrong_numbers' },
  { label: 'Wrong table', value: 'wrong_table' },
  { label: 'Misread question', value: 'misread_question' },
  { label: 'Missing context', value: 'missing_context' },
  { label: 'Bad format', value: 'bad_format' },
] as const;

interface FeedbackCommentPopoverProps {
  anchorEl: HTMLElement | null;
  pending: boolean;
  onClose: () => void;
  onSubmit: (comment: string) => void;
}

export default function FeedbackCommentPopover({ anchorEl, pending, onClose, onSubmit }: FeedbackCommentPopoverProps) {
  const [comment, setComment] = useState('');
  const open = Boolean(anchorEl);

  useEffect(() => {
    if (open) {
      setComment('');
    }
  }, [open]);

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
    >
      <Stack spacing={1} sx={{ p: 1.5, width: 300 }} data-testid="feedback-comment-popover">
        <Typography variant="caption" color="text.secondary">
          What was wrong?
        </Typography>
        <Stack direction="row" spacing={0.75} useFlexGap sx={{ flexWrap: 'wrap' }}>
          {FEEDBACK_DOWN_REASONS.map((reason) => (
            <Chip
              key={reason.value}
              label={reason.label}
              size="small"
              variant="outlined"
              clickable
              disabled={pending}
              onClick={() => onSubmit(`reason:${reason.value}`)}
              sx={{ borderRadius: 1 }}
            />
          ))}
        </Stack>
        <TextField
          autoFocus
          multiline
          minRows={2}
          size="small"
          placeholder="Add detail (optional)"
          value={comment}
          onChange={(e) => setComment(e.target.value)}
        />
        <Stack direction="row" spacing={1} justifyContent="flex-end">
          <Button size="small" onClick={onClose} disabled={pending}>
            Cancel
          </Button>
          <Button size="small" variant="contained" onClick={() => onSubmit(comment)} disabled={pending}>
            {pending ? <CircularProgress size={16} /> : 'Submit'}
          </Button>
        </Stack>
      </Stack>
    </Popover>
  );
}
